import React, { useEffect, useRef, useState } from 'react';
import { Alert, InputNumber, Modal, Typography } from 'antd';
import { instrumentConfigApi } from '../../../services/instrumentConfigApi';
import { smartBetaApi, SmartBetaView } from '../../../services/smartBetaApi';
import { formatNumber, numberInputProps } from '../../../utils/numberFormat';
import { newIdempotencyKey } from '../../../utils/idempotencyKey';
import { splitPreview, targetWeights } from '../smartBetaForm';
import { useIsMobile } from '../../../hooks/useIsMobile';

const { Text } = Typography;

interface Props {
  open: boolean;
  config: SmartBetaView;
  onCancel: () => void;
  onSuccess: (updated: SmartBetaView | null) => void;
}

const SmartBetaDepositModal: React.FC<Props> = ({ open, config, onCancel, onSuccess }) => {
  const isMobile = useIsMobile();
  const [amount, setAmount] = useState<number | null>(null);
  const [available, setAvailable] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per intent. A deposit that stopped part-way keeps it, so pressing Deposit again
  // finishes the tickers not yet funded instead of funding the others twice.
  const keyRef = useRef(newIdempotencyKey());
  const partialRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setAmount(null);
    setError(null);
    setAvailable(null);
    keyRef.current = newIdempotencyKey();
    partialRef.current = false;
    let cancelled = false;
    instrumentConfigApi
      .getDepositCapacity(config.portfolioId)
      .then((cap) => {
        if (!cancelled && cap.availableToDeposit != null) setAvailable(Number(cap.availableToDeposit));
      })
      .catch(() => undefined); // the server checks the room again on submit
    return () => {
      cancelled = true;
    };
  }, [open, config.portfolioId]);

  const weights = targetWeights(config.settings);
  const legs = splitPreview(amount ?? 0, weights);
  const tooMuch = amount != null && available != null && amount > available;

  const submit = async () => {
    if (!amount || amount <= 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await smartBetaApi.deposit(config.dataConfigId, amount, keyRef.current);
      onSuccess(res.config);
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      const msg = err.response?.data?.message ?? err.message ?? String(e);
      partialRef.current = /deposit stopped at/.test(msg);
      // Refused outright: the next press is a new deposit. Timed out or part-way: keep the key.
      if (err.response?.status != null && !partialRef.current) keyRef.current = newIdempotencyKey();
      setError(partialRef.current ? `${msg} Press Deposit again with the same amount to finish.` : msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Deposit to Smart Beta"
      open={open}
      onCancel={onCancel}
      onOk={submit}
      okText="Deposit"
      okButtonProps={{
        loading: submitting,
        disabled: !amount || tooMuch,
        style: { background: '#0ECB81', borderColor: '#0ECB81' },
      }}
      width={isMobile ? '100%' : 480}
      style={isMobile ? { top: 'auto', bottom: 0, margin: 0, maxWidth: '100vw', paddingBottom: 0 } : undefined}
      destroyOnClose
    >
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Capital now:
        </Text>{' '}
        <Text strong>{formatNumber(Number(config.totalCapital))} VND</Text>
        <br />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Available to deposit:
        </Text>{' '}
        <Text strong>{available == null ? '—' : `${formatNumber(available)} VND`}</Text>
      </div>
      <InputNumber
        {...numberInputProps()}
        min={1}
        max={available ?? undefined}
        precision={0}
        value={amount}
        onChange={(v) => {
          setAmount(v);
          // a new amount is a new intent, unless it is finishing a part-way deposit
          if (!partialRef.current) keyRef.current = newIdempotencyKey();
        }}
        onPressEnter={submit}
        addonAfter="VND"
        placeholder="Amount"
        style={{ width: '100%' }}
        autoFocus
        aria-label="Deposit amount"
      />
      {tooMuch && (
        <Text type="danger" style={{ fontSize: 12 }}>
          More than the account has free for allocation.
        </Text>
      )}
      {Object.keys(legs).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Split by weight ({config.settings.cashWeight}% cash stays uninvested at rebalance):
          </Text>
          {weights.map((w) => (
            <div
              key={w.symbol}
              style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}
            >
              <span>
                {w.symbol} <Text type="secondary">{w.weight}%</Text>
              </span>
              <span>{formatNumber(legs[w.symbol] ?? 0)} VND</span>
            </div>
          ))}
        </div>
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
    </Modal>
  );
};

export default SmartBetaDepositModal;
